import React, { useState, useRef, useEffect } from 'react';
import { User, MapPin, Target, Heart, ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { mapLevelValues, isLevelSelectionTooLong, CURRENT_LEVEL_MAX_LENGTH } from '../../utils/levelMapper';

interface CVUploadFormProps {
  onMetadataChange: (metadata: {
    career_status?: string;
    base_locations?: string;
    preferred_locations?: string;
    role_preferences?: string;
    current_role?: string;
    current_level?: string;
    is_contactable?: boolean;
  }) => void;
  isProcessing: boolean;
  preloadedData?: {
    career_status?: string;
    base_locations?: string;
    preferred_locations?: string;
    role_preferences?: string;
    current_role?: string;
    current_level?: string;
    is_contactable?: boolean;
  };
}

const CVUploadForm: React.FC<CVUploadFormProps> = ({ onMetadataChange, isProcessing, preloadedData }) => {
  const { t, i18n } = useTranslation('inspire');
  const currentLanguage = i18n.language as 'en' | 'de';
  const hasInitialized = React.useRef(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [careerStatus, setCareerStatus] = useState<string>('invisible');
  const [baseLocations, setBaseLocations] = useState<string>('');
  const [preferredLocations, setPreferredLocations] = useState<string>('');
  const [rolePreferences, setRolePreferences] = useState<string>('');
  const [currentRole, setCurrentRole] = useState<string>('');
  const [currentLevel, setCurrentLevel] = useState<string[]>([]);
  const [isLevelDropdownOpen, setIsLevelDropdownOpen] = useState(false);
  const [levelSelectionError, setLevelSelectionError] = useState<string | null>(null);
  const [isContactable, setIsContactable] = useState<boolean>(false);

  const handleLevelToggle = (optionValue: string, checked: boolean) => {
    const nextLevels = checked
      ? [...currentLevel, optionValue]
      : currentLevel.filter(level => level !== optionValue);

    if (checked && isLevelSelectionTooLong(nextLevels)) {
      setLevelSelectionError(
        t('form.currentLevelTooLong', { max: CURRENT_LEVEL_MAX_LENGTH })
      );
      return;
    }

    setLevelSelectionError(null);
    setCurrentLevel(nextLevels);
  };

  // Get level options based on current language
  const getLevelOptions = () => {
    const levels = [
      t('levels.highSchool'),
      t('levels.apprentice'),
      t('levels.bachelor'),
      t('levels.master'),
      t('levels.workingStudent'),
      t('levels.phd'),
      t('levels.qualifiedWorker'),
      t('levels.entrepreneur'),
      t('levels.junior'),
      t('levels.midLevel'),
      t('levels.senior'),
      t('levels.masterCraftsman'),
      t('levels.lead'),
      t('levels.expert')
    ];
    
    return levels.map(level => ({
      value: level,
      label: level
    }));
  };

  const handleMetadataChange = () => {
    const metadata = {
      career_status: careerStatus,
      base_locations: baseLocations.trim(), // Send empty string to clear field
      preferred_locations: preferredLocations.trim(), // Send empty string to clear field
      role_preferences: rolePreferences.trim(), // Send empty string to clear field
      current_role: currentRole.trim(), // Send empty string to clear field
      current_level: currentLevel.join(', '), // Join array to comma-separated string
      is_contactable: preloadedData?.is_contactable ?? isContactable, // Preserve existing value from parent
    };
    onMetadataChange(metadata);
  };

  // Initialize form fields from preloaded data (only once when data becomes available)
  React.useEffect(() => {
    console.debug('🔍 CVUploadForm useEffect triggered, preloadedData:', preloadedData, 'hasInitialized:', hasInitialized.current);
    if (preloadedData && !hasInitialized.current) {
      console.debug('🔍 CVUploadForm initializing with preloaded data:', preloadedData);
      console.debug('🔍 base_locations:', preloadedData.base_locations);
      console.debug('🔍 preferred_locations:', preloadedData.preferred_locations);

      setCareerStatus(preloadedData.career_status ?? 'invisible');
      setBaseLocations(preloadedData.base_locations ?? '');
      setPreferredLocations(preloadedData.preferred_locations ?? '');
      setRolePreferences(preloadedData.role_preferences ?? '');
      setCurrentRole(preloadedData.current_role ?? '');
      
      // Parse current_level: map from DB language to current UI language
      const levelString = preloadedData.current_level ?? '';
      const dbLevels = levelString ? levelString.split(',').map(l => l.trim()).filter(Boolean) : [];
      // Map DB values to current language
      const mappedLevels = mapLevelValues(dbLevels, currentLanguage);
      setCurrentLevel(mappedLevels);
      console.debug('📝 Mapped levels from DB:', dbLevels, '→', mappedLevels);
      
      setIsContactable(preloadedData.is_contactable ?? false);

      hasInitialized.current = true;
      console.debug('✅ CVUploadForm initialized with preloaded data');
    } else if (!preloadedData) {
      console.debug('ℹ️ CVUploadForm no preloadedData available yet');
    } else {
      console.debug('ℹ️ CVUploadForm already initialized, skipping');
    }
  }, [preloadedData]); // React to preloadedData changes but only initialize once

  // Update metadata whenever any field changes
  React.useEffect(() => {
    handleMetadataChange();
  }, [careerStatus, baseLocations, preferredLocations, rolePreferences, currentRole, currentLevel, isContactable]);

  // Remap levels when language changes (so dropdown values match new language)
  React.useEffect(() => {
    if (currentLevel.length > 0 && hasInitialized.current) {
      // Determine the "from" language (opposite of current)
      const fromLanguage = currentLanguage === 'en' ? 'de' : 'en';
      const remappedLevels = mapLevelValues(currentLevel, currentLanguage);
      
      // Only update if actually different (avoid infinite loop)
      if (JSON.stringify(remappedLevels) !== JSON.stringify(currentLevel)) {
        console.debug('🌍 Language changed - remapping levels:', currentLevel, '→', remappedLevels);
        setCurrentLevel(remappedLevels);
      }
    }
  }, [currentLanguage]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsLevelDropdownOpen(false);
      }
    };

    if (isLevelDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isLevelDropdownOpen]);

  return (
    <div className="space-y-4 sm:space-y-6">

      {/* Career Status */}
      <div className="space-y-1.5 sm:space-y-2">
        <label className="block text-xs sm:text-sm font-medium text-gray-700">
          <User className="inline mr-1 w-3 h-3 sm:w-3.5 sm:h-3.5" />
          {t('form.careerStatusLabel')}
        </label>
        <div className="relative">
          <select
            value={careerStatus}
            onChange={(e) => setCareerStatus(e.target.value)}
            disabled={isProcessing}
            className="w-full px-2.5 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 appearance-none bg-white pr-8 sm:pr-10"
          >
          <option value="actively_seeking">{t('form.careerStatusReady')}</option>
          <option value="curious">{t('form.careerStatusCurious')}</option>
          <option value="invisible">{t('form.careerStatusInvisible')}</option>
          </select>
          <ChevronDown className="absolute right-2.5 sm:right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none w-3.5 h-3.5 sm:w-4 sm:h-4" />
        </div>
        <p className="text-[10px] sm:text-xs text-gray-500">
          {t('form.careerStatusHint')}
        </p>
      </div>

      {/* Base Locations */}
      <div className="space-y-1.5 sm:space-y-2">
        <label className="block text-xs sm:text-sm font-medium text-gray-700">
          <MapPin className="inline mr-1 w-3 h-3 sm:w-3.5 sm:h-3.5" />
          {t('form.baseLocationsLabel')}
        </label>
        <input
          type="text"
          value={baseLocations}
          onChange={(e) => setBaseLocations(e.target.value)}
          disabled={isProcessing}
          placeholder={t('form.baseLocationsPlaceholder')}
          className="w-full px-2.5 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-400 focus:border-gray-400 disabled:opacity-50 placeholder:text-[10px] sm:placeholder:text-xs"
          maxLength={500}
        />
        <p className="text-[10px] sm:text-xs text-gray-500">
          {t('form.baseLocationsHint')}
        </p>
      </div>

      {/* Preferred Locations */}
      <div className="space-y-1.5 sm:space-y-2">
        <label className="block text-xs sm:text-sm font-medium text-gray-700">
          <Target className="inline mr-1 w-3 h-3 sm:w-3.5 sm:h-3.5" />
          {t('form.preferredLocationsLabel')}
        </label>
        <input
          type="text"
          value={preferredLocations}
          onChange={(e) => setPreferredLocations(e.target.value)}
          disabled={isProcessing}
          placeholder={t('form.preferredLocationsPlaceholder')}
          className="w-full px-2.5 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-400 focus:border-gray-400 disabled:opacity-50 placeholder:text-[10px] sm:placeholder:text-xs"
          maxLength={500}
        />
        <p className="text-[10px] sm:text-xs text-gray-500">
          {t('form.preferredLocationsHint')}
        </p>
      </div>

      {/* Role Preferences */}
      <div className="space-y-1.5 sm:space-y-2">
        <label className="block text-xs sm:text-sm font-medium text-gray-700">
          <Heart className="inline mr-1 w-3 h-3 sm:w-3.5 sm:h-3.5" />
          {t('form.rolePreferencesLabel')}
        </label>
        <textarea
          value={rolePreferences}
          onChange={(e) => setRolePreferences(e.target.value)}
          disabled={isProcessing}
          placeholder={t('form.rolePreferencesPlaceholder')}
          rows={3}
          maxLength={500}
          className="w-full px-2.5 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-400 focus:border-gray-400 disabled:opacity-50 resize-none placeholder:text-[10px] sm:placeholder:text-xs"
        />
        <p className="text-[10px] sm:text-xs text-gray-500">
          {t('form.rolePreferencesHint')}
        </p>
      </div>

      {/* Current Role & Level - Side by Side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
        {/* Current Role */}
        <div className="space-y-1.5 sm:space-y-2">
          <label className="block text-xs sm:text-sm font-medium text-gray-700">
            <User className="inline mr-1 w-3 h-3 sm:w-3.5 sm:h-3.5" />
            {t('form.currentRoleLabel')}
          </label>
          <input
            type="text"
            value={currentRole}
            onChange={(e) => setCurrentRole(e.target.value)}
            disabled={isProcessing}
            placeholder={t('form.currentRolePlaceholder')}
            className="w-full px-2.5 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-400 focus:border-gray-400 disabled:opacity-50 placeholder:text-[10px] sm:placeholder:text-xs"
            maxLength={200}
          />
          <p className="text-[10px] sm:text-xs text-gray-500">
            {t('form.currentRoleHint')}
          </p>
        </div>

        {/* Current Level - Multi-select */}
        <div className="space-y-1.5 sm:space-y-2">
          <label className="block text-xs sm:text-sm font-medium text-gray-700">
            <Target className="inline mr-1 w-3 h-3 sm:w-3.5 sm:h-3.5" />
            {t('form.currentLevelLabel')}
          </label>
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setIsLevelDropdownOpen(!isLevelDropdownOpen)}
              disabled={isProcessing}
              className="w-full px-2.5 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 bg-white text-left pr-8 sm:pr-10"
            >
              <span className={currentLevel.length === 0 ? 'text-gray-400 text-[10px] sm:text-xs' : 'text-gray-900'}>
                {currentLevel.length === 0 
                  ? t('form.currentLevelPlaceholder')
                  : currentLevel.length === 1
                  ? (() => {
                      // Show full label for single selection
                      // Values are now display-ready (no mapping needed)
                      return currentLevel[0];
                    })()
                  : t('form.currentLevelSelected', { count: currentLevel.length })
                }
              </span>
            </button>
            <ChevronUp className="absolute right-2.5 sm:right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none w-3.5 h-3.5 sm:w-4 sm:h-4" />
            
            {isLevelDropdownOpen && (
              <div className="absolute z-10 bottom-full mb-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                {getLevelOptions().map((option) => (
                  <label
                    key={option.value}
                    className="flex items-center px-2.5 py-1.5 sm:px-3 sm:py-2 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={currentLevel.includes(option.value)}
                      onChange={(e) => handleLevelToggle(option.value, e.target.checked)}
                      className="mr-2 h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-600 focus:ring-gray-400 border-gray-300 rounded"
                    />
                    <span className="text-xs sm:text-sm text-gray-700">{option.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <p className="text-[10px] sm:text-xs text-gray-500">
            {t('form.currentLevelHint')}
          </p>
          {levelSelectionError && (
            <p className="text-[10px] sm:text-xs text-red-600">{levelSelectionError}</p>
          )}
        </div>
      </div>

      {/* Info Box */}
      <div className="p-2.5 sm:p-3 bg-blue-50 border border-blue-200 rounded-md">
        <p className="text-[11px] sm:text-sm text-blue-800" dangerouslySetInnerHTML={{ __html: t('form.infoNote') }} />
      </div>
    </div>
  );
};

export default CVUploadForm;
